// Build: 2026-01-25 - Implemented Lazy Loading for Performance Optimization
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login'; // Eager load Login for immediate access

// Lazy load all other pages for code splitting
const SuperAdminDashboard = lazy(() => import('./pages/super-admin-dashboard'));
const GMDashboard = lazy(() => import('./pages/GMDashboard'));
const ManagerDashboard = lazy(() => import('./pages/manager-dashboard'));
const MedicalRepDashboard = lazy(() => import('./pages/MedicalRepDashboard'));
const Clinics = lazy(() => import('./pages/clinics'));
const ClinicDetails = lazy(() => import('./pages/clinic-details'));
const Visits = lazy(() => import('./pages/visits'));
const VisitDetail = lazy(() => import('./pages/visit-detail'));
const Orders = lazy(() => import('./pages/orders'));
const Expenses = lazy(() => import('./pages/expenses'));
const Users = lazy(() => import('./pages/users'));
const Products = lazy(() => import('./pages/Products'));
const Approvals = lazy(() => import('./pages/Approvals'));
const GPSTracking = lazy(() => import('./pages/gps-tracking'));
const PerformanceDashboard = lazy(() => import('./pages/performance-dashboard'));
const Settings = lazy(() => import('./pages/settings'));
const Returns = lazy(() => import('./pages/Returns'));
const Accounting = lazy(() => import('./pages/accounting'));
const Plans = lazy(() => import('./pages/plans'));
const Notifications = lazy(() => import('./pages/notifications'));
const AdminLocationMap = lazy(() => import('./pages/AdminLocationMap'));
// Splash Screen Component - Re-enabled with pure React (no Capacitor dependency)
import SplashScreenComponent from './components/SplashScreenComponent';
// DISABLED: OfflineBanner uses Capacitor Network plugin
import OfflineBanner from './components/OfflineBanner'; // Re-enabled with pure web API
import { Toaster } from './components/ui/sonner';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ErrorBoundary from './components/ErrorBoundary';
import notificationPollingService from './utils/NotificationPollingService';
import { requestAllPermissions } from './utils/androidPermissions';
import { initPushNotifications, sendTokenToBackend, removePushListeners } from './services/pushNotifications';
import { warmupGPS } from './utils/gps';
import { initializeTracking, startBackgroundTracking } from './services/BackgroundGPS';
import './App.css';


const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [splashReady, setSplashReady] = useState(false); // Show splash screen first

  useEffect(() => {
    // Only check auth after splash screen is ready
    if (splashReady) {
      // Request Android permissions at app startup
      requestAllPermissions().then(() => { });

      // 🛰️ Pre-warm GPS for faster location captures throughout the app
      warmupGPS();

      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (token && userData) {
        setUser(JSON.parse(userData));

        // Start notification polling for existing session
        notificationPollingService.initializeChannel();
        notificationPollingService.requestPermissions();
        notificationPollingService.start();
      }
      setLoading(false);
    }
  }, [splashReady]);

  const handleLogin = async (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);

    // Start notification polling service
    notificationPollingService.initializeChannel();
    notificationPollingService.requestPermissions();
    notificationPollingService.start();

    // Initialize Firebase Push Notifications (for real push when app is in background)
    try {
      await initPushNotifications((fcmToken) => {
        // Send FCM token to backend for this user
        sendTokenToBackend(fcmToken, userData.id);
      });
    } catch (err) {
      console.error('[App] Firebase Push init error (non-critical):', err.message);
    }

    // Start background GPS tracking for medical reps
    if (userData.role === 'medical_rep') {
      try {
        await startBackgroundTracking();
      } catch (err) {
        console.error('[App] GPS tracking error (non-critical):', err.message);
      }
    }
  };

  const handleLogout = async () => {
    // Stop notification polling service
    notificationPollingService.stop();

    // Remove Firebase Push listeners
    try {
      await removePushListeners();
    } catch (err) {
      console.error('[App] Push cleanup error:', err.message);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  // Show custom splash screen until ready
  if (!splashReady) {
    return <SplashScreenComponent onReady={() => setSplashReady(true)} />;
  }

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    if (allowedRoles && !allowedRoles.includes(user.role)) {
      return <Navigate to="/" replace />;
    }
    return children;
  };

  const DashboardRoute = () => {
    if (!user) return <Navigate to="/login" replace />;

    switch (user.role) {
      case 'super_admin':
        return <SuperAdminDashboard user={user} onLogout={handleLogout} />;
      case 'gm':
        return <GMDashboard user={user} onLogout={handleLogout} />;
      case 'manager':
        return <ManagerDashboard user={user} onLogout={handleLogout} />;
      case 'accountant':
        return <Orders user={user} onLogout={handleLogout} />;
      case 'medical_rep':
        return <MedicalRepDashboard user={user} onLogout={handleLogout} />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <ThemeProvider>
      <SiteSettingsProvider>
        <ErrorBoundary>
          <BrowserRouter>
            <PermissionsProvider user={user}>
              <Toaster position="top-right" />
              <OfflineBanner />
              <Suspense fallback={
                <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
                    <p className="text-slate-600 font-medium">جاري التحميل...</p>
                  </div>
                </div>
              }>
                <Routes>
                  <Route
                    path="/login"
                    element={user ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />}
                  />
                  <Route path="/" element={<DashboardRoute />} />
                  <Route
                    path="/clinics"
                    element={
                      <ProtectedRoute>
                        <Clinics user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/clinics/:id"
                    element={
                      <ProtectedRoute>
                        <ClinicDetails user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/visits"
                    element={
                      <ProtectedRoute>
                        <Visits user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/visits/:serialNumber"
                    element={
                      <ProtectedRoute>
                        <VisitDetail user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/orders"
                    element={
                      <ProtectedRoute>
                        <Orders user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/expenses"
                    element={
                      <ProtectedRoute>
                        <Expenses user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <ProtectedRoute allowedRoles={['super_admin', 'gm', 'manager']}>
                        <Users user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/products"
                    element={
                      <ProtectedRoute allowedRoles={['super_admin']}>
                        <Products user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/approvals"
                    element={
                      <ProtectedRoute allowedRoles={['manager', 'gm', 'super_admin']}>
                        <Approvals user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/gps-tracking"
                    element={
                      <ProtectedRoute allowedRoles={['super_admin']}>
                        <GPSTracking user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }

                  />
                  <Route
                    path="/performance"
                    element={
                      <ProtectedRoute allowedRoles={['super_admin', 'gm', 'manager']}>
                        <PerformanceDashboard user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute allowedRoles={['super_admin', 'gm']}>
                        <Settings user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/returns"
                    element={
                      <ProtectedRoute allowedRoles={['accountant', 'super_admin']}>
                        <Returns user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/accounting"
                    element={
                      <ProtectedRoute allowedRoles={['accountant', 'gm', 'super_admin']}>
                        <Accounting user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/plans"
                    element={
                      <ProtectedRoute>
                        <Plans user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <Notifications user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rep-locations"
                    element={
                      <ProtectedRoute allowedRoles={['super_admin', 'gm']}>
                        <AdminLocationMap user={user} onLogout={handleLogout} />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </PermissionsProvider>
          </BrowserRouter>
        </ErrorBoundary>
      </SiteSettingsProvider>
    </ThemeProvider>
  );
};

export default App;